import {
  meaningful,
  type SimuladoSourceQuestion,
} from '@/modules/simulado/domain/entities/question.entity';
import type { Answers } from '@/modules/simulado/domain/entities/answer.entity';
import type { AnswerKeyEntry } from './answer-key.entity';

/** Acertos de um grupo de questões: quantas a pessoa acertou de quantas valem. */
export interface GroupScore {
  name: string;
  correct: number;
  /** Questões que contam: nem anuladas, nem sem resposta divulgada. */
  total: number;
}

/** Uma grande área (H1 da classificação) e as subáreas dela (H2). */
export interface AreaScore extends GroupScore {
  subareas: GroupScore[];
}

/** Do mais cobrado para o menos; empate, em ordem alfabética. */
function byWeight(a: GroupScore, b: GroupScore): number {
  return b.total - a.total || a.name.localeCompare(b.name, 'pt-BR');
}

/**
 * Acertos por **grande área** (H1 da árvore de classificação, o `area` da
 * questão) e, dentro de cada uma, por **H2** (o `specialty`).
 *
 * Mesma régua da nota bruta: o gabarito oficial (`answerKey`) decide o que
 * conta — anulada e sem resposta divulgada ficam fora —, e em branco conta
 * como erro. Questão sem grande área fica fora do quadro (não há onde pôr);
 * com grande área mas sem H2, conta só na área.
 *
 * Devolve `[]` quando nenhuma questão tem classificação: a tela então não
 * mostra a seção, em vez de um quadro vazio.
 */
export function breakdownByArea(
  questions: readonly SimuladoSourceQuestion[],
  answerKey: readonly AnswerKeyEntry[],
  answers: Answers,
): AreaScore[] {
  const keyByNumber = new Map(answerKey.map((entry) => [entry.number, entry]));
  const areas = new Map<
    string,
    { correct: number; total: number; subareas: Map<string, GroupScore> }
  >();

  for (const question of questions) {
    const entry = keyByNumber.get(question.number);
    if (!entry || entry.isExcluded || entry.answer === null) continue;
    const areaName = meaningful(question.area);
    if (!areaName) continue;

    const hit = answers[question.number] === entry.answer ? 1 : 0;
    const area = areas.get(areaName) ?? {
      correct: 0,
      total: 0,
      subareas: new Map<string, GroupScore>(),
    };
    area.correct += hit;
    area.total += 1;

    const subName = meaningful(question.specialty);
    if (subName) {
      const sub = area.subareas.get(subName) ?? {
        name: subName,
        correct: 0,
        total: 0,
      };
      sub.correct += hit;
      sub.total += 1;
      area.subareas.set(subName, sub);
    }
    areas.set(areaName, area);
  }

  return [...areas.entries()]
    .map(([name, area]) => ({
      name,
      correct: area.correct,
      total: area.total,
      subareas: [...area.subareas.values()].sort(byWeight),
    }))
    .sort(byWeight);
}

/** Percentual de acerto, arredondado; `0` quando o grupo não tem questão. */
export function hitRate(group: GroupScore): number {
  return group.total === 0
    ? 0
    : Math.round((group.correct / group.total) * 100);
}
